/**
 * Import commit engine — turns validated BuiltRecords into real records +
 * submissions via the same RPCs the wizard uses (`create_record`, `submit_form`),
 * so all RLS, optimistic locking, and status transitions apply unchanged.
 *
 * Record identity:
 *   - create_new        : every record group → a fresh record.
 *   - match_display_id  : look up by display_id; upsert (create if missing).
 *   - match_field       : look up the record via a submission field value; upsert.
 *
 * Instance indexing continues after any existing instances on a matched record.
 * Single-instance forms update the existing submission (instance_index = 1)
 * with optimistic-lock version. Instances with blocking errors are skipped
 * defensively (the review grid should have caught them first).
 */
import { supabase } from '@/lib/supabase/client'
import { submitForm } from '@/features/records/api/submissions'
import { createRecord } from '@/features/records/api/records-crud'
import { serializeSoftWarningFlags, justificationKey } from '@/lib/form-engine'
import { validateInstance } from '../lib/validate-import'
import type { BuiltRecord } from '../lib/build-records'
import type { CommitStatus } from '../lib/mapping-types'
import type {
  FieldDefinition,
  ConditionalRule,
} from '@/features/form-builder/api/form-builder-types'

export interface ImportRowResult {
  recordIndex: number
  sourceRowIndex: number
  displayId?: string
  action: 'created' | 'updated' | 'failed' | 'skipped'
  error?: string
}

export interface ImportCommitResult {
  recordsCreated: number
  recordsMatched: number
  submissionsCreated: number
  submissionsUpdated: number
  failed: number
  skipped: number
  rows: ImportRowResult[]
  createdDisplayIds: string[]
  jobId?: string
}

export interface ImportCommitArgs {
  projectId: string
  userId: string
  formId: string
  formVersionId: string
  instanceType: 'single' | 'repeatable'
  fields: FieldDefinition[]
  conditionalRules: ConditionalRule[]
  records: BuiltRecord[]
  commitStatus: CommitStatus
  fileName: string
  fileFormat: 'csv' | 'xlsx'
  mappingId?: string | null
  onProgress?: (done: number, total: number) => void
}

interface ResolvedTarget {
  recordId: string
  displayId: string
  created: boolean
  /** Next instance_index to use for a NEW submission on this record. */
  nextIndex: number
  /** For single forms: existing submission to update (if any). */
  existingSubmission?: { id: string; version: number; index: number }
}

async function findRecordByDisplayId(projectId: string, displayId: string) {
  const { data } = await supabase
    .from('records')
    .select('id, display_id')
    .eq('project_id', projectId)
    .eq('display_id', displayId)
    .eq('is_deleted', false)
    .maybeSingle()
  return data
}

async function findRecordByField(formId: string, fieldKey: string, value: string) {
  const { data } = await supabase
    .from('submissions')
    .select('record_id, records!inner(display_id, project_id, is_deleted)')
    .eq('form_id', formId)
    .eq('is_deleted', false)
    .eq(`field_values->>${fieldKey}`, value)
    .limit(1)
    .maybeSingle()
  if (!data) return null
  const rec = data.records as unknown as { display_id: string; is_deleted: boolean }
  if (rec.is_deleted) return null
  return { id: data.record_id as string, display_id: rec.display_id }
}

/** Existing non-deleted submissions for (record, form), to continue numbering / update. */
async function loadExistingSubmissions(recordId: string, formId: string) {
  const { data } = await supabase
    .from('submissions')
    .select('id, version, instance_index')
    .eq('record_id', recordId)
    .eq('form_id', formId)
    .eq('is_deleted', false)
    .order('instance_index')
  return (data ?? []) as Array<{ id: string; version: number; instance_index: number }>
}

async function resolveTarget(
  rec: BuiltRecord,
  args: ImportCommitArgs,
): Promise<ResolvedTarget & { matched: boolean }> {
  const { projectId, userId, formId, instanceType } = args
  let recordId: string | null = null
  let displayId = ''
  let matched = false

  if (rec.identity.mode === 'match_display_id' && rec.identity.displayId) {
    const found = await findRecordByDisplayId(projectId, rec.identity.displayId)
    if (found) {
      recordId = found.id
      displayId = found.display_id
      matched = true
    }
  } else if (rec.identity.mode === 'match_field' && rec.identity.matchFieldKey && rec.identity.matchValue) {
    const found = await findRecordByField(formId, rec.identity.matchFieldKey, rec.identity.matchValue)
    if (found) {
      recordId = found.id
      displayId = found.display_id
      matched = true
    }
  }

  // create_new, or match miss → upsert by creating.
  if (!recordId) {
    const created = await createRecord(projectId, userId)
    return {
      recordId: created.id,
      displayId: created.display_id,
      created: true,
      matched: false,
      nextIndex: 1,
      existingSubmission: undefined,
    }
  }

  const existing = await loadExistingSubmissions(recordId, formId)
  const nextIndex = existing.length ? Math.max(...existing.map((e) => e.instance_index)) + 1 : 1

  // Single-instance forms update the existing instance_index=1 submission.
  let existingSubmission: ResolvedTarget['existingSubmission']
  if (instanceType === 'single') {
    const slot = existing.find((e) => e.instance_index === 1)
    if (slot) existingSubmission = { id: slot.id, version: slot.version, index: slot.instance_index }
  }

  return { recordId, displayId, created: false, matched, nextIndex, existingSubmission }
}

export async function runImportCommit(args: ImportCommitArgs): Promise<ImportCommitResult> {
  const result: ImportCommitResult = {
    recordsCreated: 0,
    recordsMatched: 0,
    submissionsCreated: 0,
    submissionsUpdated: 0,
    failed: 0,
    skipped: 0,
    rows: [],
    createdDisplayIds: [],
  }

  const totalInstances = args.records.reduce((n, r) => n + r.instances.length, 0)
  let done = 0
  const submit = args.commitStatus === 'submitted'

  for (let ri = 0; ri < args.records.length; ri++) {
    const rec = args.records[ri]

    let target: (ResolvedTarget & { matched: boolean }) | null = null
    try {
      target = await resolveTarget(rec, args)
      if (target.created) {
        result.recordsCreated++
        result.createdDisplayIds.push(target.displayId)
      } else {
        result.recordsMatched++
      }
    } catch (e) {
      // Whole record failed to resolve → mark every instance failed.
      for (const inst of rec.instances) {
        result.failed++
        done++
        result.rows.push({
          recordIndex: ri,
          sourceRowIndex: inst.sourceRowIndex,
          action: 'failed',
          error: (e as Error).message,
        })
      }
      args.onProgress?.(done, totalInstances)
      continue
    }

    let useExisting = target.existingSubmission // single-form update slot (consumed once)

    for (const inst of rec.instances) {
      done++
      // Defensive validation — never push blocking-invalid data.
      const v = validateInstance(args.fields, args.conditionalRules, inst)
      if (v.hasBlocking) {
        result.skipped++
        result.rows.push({
          recordIndex: ri,
          sourceRowIndex: inst.sourceRowIndex,
          displayId: target.displayId,
          action: 'skipped',
          error: Object.values(v.blockingErrors)[0]?.message ?? 'دارای خطای اعتبارسنجی',
        })
        args.onProgress?.(done, totalInstances)
        continue
      }

      const isUpdate = !!useExisting

      // When finalizing (submit), soft warnings require a persisted justification
      // for the audit trail. Auto-justify imported rows so the trail is honest
      // rather than silently empty (spec §06 soft-warning compliance).
      let softWarningFlags: unknown[] = []
      if (submit && v.softWarnings.length > 0) {
        const justifications: Record<string, string> = {}
        for (const w of v.softWarnings) {
          justifications[justificationKey(w.fieldKey, w.ruleId)] = `وارد شده از فایل «${args.fileName}»`
        }
        softWarningFlags = serializeSoftWarningFlags({
          liveWarnings: v.softWarnings,
          justifications,
          previousFlags: [],
          userId: args.userId,
        })
      }

      try {
        await submitForm({
          submissionId: useExisting?.id ?? null,
          recordId: target.recordId,
          formId: args.formId,
          formVersionId: args.formVersionId,
          instanceIndex: useExisting?.index ?? target.nextIndex,
          expectedVersion: useExisting?.version ?? 0,
          userId: args.userId,
          projectId: args.projectId,
          fieldValues: inst.fieldValues,
          softWarningFlags,
          submit,
        })
        if (isUpdate) {
          result.submissionsUpdated++
          result.rows.push({ recordIndex: ri, sourceRowIndex: inst.sourceRowIndex, displayId: target.displayId, action: 'updated' })
          useExisting = undefined // only one update slot for single forms
        } else {
          result.submissionsCreated++
          result.rows.push({ recordIndex: ri, sourceRowIndex: inst.sourceRowIndex, displayId: target.displayId, action: 'created' })
          target.nextIndex++
        }
      } catch (e) {
        result.failed++
        result.rows.push({
          recordIndex: ri,
          sourceRowIndex: inst.sourceRowIndex,
          displayId: target.displayId,
          action: 'failed',
          error: (e as Error).message,
        })
      }
      args.onProgress?.(done, totalInstances)
    }
  }

  // Audit row.
  const status =
    result.failed === 0 && result.skipped === 0
      ? 'completed'
      : result.submissionsCreated + result.submissionsUpdated > 0
        ? 'partial'
        : 'failed'
  try {
    const { data: job } = await supabase
      .from('import_jobs')
      .insert({
        project_id: args.projectId,
        form_id: args.formId,
        mapping_id: args.mappingId ?? null,
        created_by: args.userId,
        file_name: args.fileName,
        file_format: args.fileFormat,
        commit_status: args.commitStatus,
        status,
        row_count: totalInstances,
        records_created: result.recordsCreated,
        records_matched: result.recordsMatched,
        submissions_created: result.submissionsCreated,
        submissions_updated: result.submissionsUpdated,
        failed_count: result.failed + result.skipped,
        result: {
          createdDisplayIds: result.createdDisplayIds,
          failures: result.rows.filter((r) => r.action === 'failed' || r.action === 'skipped'),
        },
      })
      .select('id')
      .single()
    result.jobId = job?.id
  } catch {
    // Audit insert failure must not lose the import outcome — swallow.
  }

  return result
}
